import { List, ListItem } from '@ui/list/list';
import { PersonIcon } from '@ui/icons/material/Person';
import { Trans } from '@ui/i18n/trans';
import { PhonelinkLockIcon } from '@ui/icons/material/PhonelinkLock';
import { LanguageIcon } from '@ui/icons/material/Language';
import { DangerousIcon } from '@ui/icons/material/Dangerous';
import { ReactNode, useContext, useState, useEffect } from 'react';
import { DevicesIcon } from '@ui/icons/material/Devices';
import { SiteConfigContext } from '@common/core/settings/site-config-context';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { SubscriptionsIcon } from '@ui/icons/material/Subscriptions';

export enum AccountSettingsId {
  AccountDetails = 'account-details',
  SocialLogin = 'social-login',
  Password = 'password',
  TwoFactor = 'two-factor',
  LocationAndLanguage = 'location-and-language',
  Developers = 'developers',
  DeleteAccount = 'delete-account',
  Sessions = 'sessions',
  Subscription = 'subscription',
}

interface Props {
  selectedTab: AccountSettingsId;
  onTabChange: (id: AccountSettingsId) => void;
}

export function AccountSettingsSidenav({ selectedTab, onTabChange }: Props) {
  const p = AccountSettingsId;
  const { auth } = useContext(SiteConfigContext);

  return (
    <aside className="sticky top-20 hidden flex-shrink-0 w-280 lg:block mr-40">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="rounded border border-white/5 bg-white/[0.02] p-12 backdrop-blur-xl shadow-xl"
      >
        <List padding="p-0">
          {auth.accountSettingsPanels?.map(panel => (
            <Item
              key={panel.id}
              icon={<panel.icon viewBox="0 0 50 50" />}
              panel={panel.id as AccountSettingsId}
              isActive={selectedTab === panel.id}
              onClick={() => onTabChange(panel.id as AccountSettingsId)}
            >
              <Trans {...panel.label} />
            </Item>
          ))}
          <Item
            icon={<PersonIcon />}
            panel={p.AccountDetails}
            isActive={selectedTab === p.AccountDetails}
            onClick={() => onTabChange(p.AccountDetails)}
          >
            <Trans message="Account details" />
          </Item>
          <Item
            icon={<PhonelinkLockIcon />}
            panel={p.TwoFactor}
            isActive={selectedTab === p.TwoFactor}
            onClick={() => onTabChange(p.TwoFactor)}
          >
            <Trans message="Two factor authentication" />
          </Item>
          <Item
            icon={<DevicesIcon />}
            panel={p.Sessions}
            isActive={selectedTab === p.Sessions}
            onClick={() => onTabChange(p.Sessions)}
          >
            <Trans message="Active sessions" />
          </Item>
          <Item
            icon={<LanguageIcon />}
            panel={p.LocationAndLanguage}
            isActive={selectedTab === p.LocationAndLanguage}
            onClick={() => onTabChange(p.LocationAndLanguage)}
          >
            <Trans message="Location and language" />
          </Item>
          <Item
            icon={<SubscriptionsIcon />}
            panel={p.Subscription}
            isActive={selectedTab === p.Subscription}
            onClick={() => onTabChange(p.Subscription)}
          >
            <Trans message="Subscription" />
          </Item>
          <Item
            icon={<DangerousIcon />}
            panel={p.DeleteAccount}
            isActive={selectedTab === p.DeleteAccount}
            onClick={() => onTabChange(p.DeleteAccount)}
          >
            <Trans message="Delete account" />
          </Item>
        </List>
      </motion.div>
    </aside>
  );
}

export function AccountSettingsMobileNav({ selectedTab, onTabChange }: Props) {
  const p = AccountSettingsId;
  const { auth } = useContext(SiteConfigContext);

  return (
    <div className="mb-32 overflow-x-auto lg:hidden -mx-24 px-24 scrollbar-hide">
      <div className="flex items-center gap-8 min-w-max pb-4">
        {auth.accountSettingsPanels?.map(panel => (
          <MobileItem
            key={panel.id}
            icon={<panel.icon className="w-18 h-18" />}
            isActive={selectedTab === panel.id}
            onClick={() => onTabChange(panel.id as AccountSettingsId)}
          >
            <Trans {...panel.label} />
          </MobileItem>
        ))}
        <MobileItem
          icon={<PersonIcon className="w-18 h-18" />}
          isActive={selectedTab === p.AccountDetails}
          onClick={() => onTabChange(p.AccountDetails)}
        >
          <Trans message="Details" />
        </MobileItem>
        <MobileItem
          icon={<PhonelinkLockIcon className="w-18 h-18" />}
          isActive={selectedTab === p.TwoFactor}
          onClick={() => onTabChange(p.TwoFactor)}
        >
          <Trans message="2FA" />
        </MobileItem>
        <MobileItem
          icon={<DevicesIcon className="w-18 h-18" />}
          isActive={selectedTab === p.Sessions}
          onClick={() => onTabChange(p.Sessions)}
        >
          <Trans message="Sessions" />
        </MobileItem>
        <MobileItem
          icon={<LanguageIcon className="w-18 h-18" />}
          isActive={selectedTab === p.LocationAndLanguage}
          onClick={() => onTabChange(p.LocationAndLanguage)}
        >
          <Trans message="Region" />
        </MobileItem>
        <MobileItem
          icon={<SubscriptionsIcon className="w-18 h-18" />}
          isActive={selectedTab === p.Subscription}
          onClick={() => onTabChange(p.Subscription)}
        >
          <Trans message="Plans" />
        </MobileItem>
        <MobileItem
          icon={<DangerousIcon className="w-18 h-18" />}
          isActive={selectedTab === p.DeleteAccount}
          onClick={() => onTabChange(p.DeleteAccount)}
        >
          <Trans message="Account" />
        </MobileItem>
      </div>
    </div>
  );
}

interface MobileItemProps {
  children: ReactNode;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
}

function MobileItem({ children, icon, isActive, onClick }: MobileItemProps) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'relative flex items-center gap-8 rounded px-16 py-10 text-sm font-medium transition-all outline-none',
        isActive ? 'text-white' : 'text-white/40 hover:text-white/70'
      )}
    >
      <span className="relative z-10">{icon}</span>
      <span className="relative z-10 whitespace-nowrap">{children}</span>
      {isActive && (
        <motion.div
          layoutId="active-pill-mobile"
          className="absolute inset-0 z-0 bg-primary rounded shadow-[0_0_20px_rgba(var(--be-primary),0.3)]"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        />
      )}
    </button>
  );
}

interface ItemProps {
  children: ReactNode;
  icon: ReactNode;
  panel: AccountSettingsId;
  isActive: boolean;
  onClick: () => void;
}

function Item({ children, icon, panel, isActive, onClick }: ItemProps) {
  return (
    <ListItem
      startIcon={icon}
      className={clsx(
        'relative mb-4 overflow-hidden rounded transition-all duration-300',
        isActive ? 'text-primary' : 'text-white/60 hover:bg-white/5 hover:text-white/90'
      )}
      onSelected={() => onClick()}
    >
      <span className="relative z-10 font-medium">{children}</span>
      {isActive && (
        <motion.div
          layoutId="active-pill"
          className="absolute inset-0 z-0 bg-primary/10 border-l-4 border-primary"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        />
      )}
    </ListItem>
  );
}
